import { Box } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IS_SA } from '../../../../../../config/appConfig';
import { AppRoutes } from '../../../../../../constants/appRoutes';
import { FormHelper } from '../../../../../../helpers/formHelper';
import { buildLocalizeRoute } from '../../../../../../helpers/languageHelper';
import {
	getRegistrationData,
	saveTokenFromRegistration,
	serverNowFrom,
	setRegistrationData,
	updateInvestorSteps,
} from '../../../../../../services/authService';
import { ArrowIcons } from '../../../../../../shared/components/common/arrowIcons';
import { ButtonWrapper } from '../../../../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../../../../shared/components/formUtilities/useInput';
import SelectWrapper from '../../../../../../shared/components/formUtilities/useSelect';
import { InvestorRegistrationBusinesSchema } from '../../../../../../shared/validations/schema';
import { InvestorStepperContext } from '../../../../../../context/investorStepperContext';
import { SubStepper } from '../../../../components/subStepper';
import { InvestorRegService } from '../../../investorRegService';
import { InvestorDeclarationDialog } from '../../declarationDialog';

export const InvestorBusinessInformation = ({
	onGoBack = () => { },
	isLoading = false,
}) => {
	const investorRegService = new InvestorRegService();
	const { investorCollections } = useSelector(
		(state) => state.investorCollectionReducer
	);

	const navigate = useNavigate();
	const { t } = useTranslation();
	const [showDeclarationPopup, setShowDeclarationPopup] = useState(false);
	const [loading, setLoading] = useState(isLoading);
	const { isKsaInternationalInvestor } = useContext(InvestorStepperContext);

	const countries = investorCollections?.countryCollection;
	const crsWhyNotQuestionsList = investorCollections?.crsWhyNotQuestionsCollection || [];

	const form = useFormik({
		initialValues: {
			businessName: '',
			tradeLicenseNumber: '',
			registrationCountryId: '',
			taxResidencies: FormHelper.getTaxResidencies([], 2)
		},
		validationSchema: InvestorRegistrationBusinesSchema,
		onSubmit: (values) => {
			submit(values);
		},
		...FormHelper.formikProps(),
	});

	useEffect(() => {
		investorRegService
			.getInvestorProfileBusinessInfo()
			.then((resp) => {
				const params = new URLSearchParams(window.location.search);
				const countryOfResidenceId = params.get('c') ? parseInt(params.get('c')) : null;
				let businessInfo = resp.data;
				let businessInfoData = {
					...businessInfo,
					tradeLicenseNumber: businessInfo?.tradeLicenseNumber || '',
					businessName: businessInfo?.businessName || '',
					registrationCountryId: businessInfo?.registrationCountry?.id,
					taxResidencies: FormHelper.getTaxResidencies(businessInfo?.taxResidencies, 2)
				};
				if (countryOfResidenceId && (countryOfResidenceId === 1 || countryOfResidenceId === 2)) {
					if (businessInfoData?.taxResidencies?.length === 1 && !businessInfoData?.taxResidencies[0]?.countryId) {
						businessInfoData.taxResidencies[0].countryId = countryOfResidenceId;
						businessInfoData.taxResidencies[0].hasTaxId = false;
						businessInfoData.taxResidencies[0].whyNotId = 1;
					}
				}
				setTimeout(() => {
					form.setValues(businessInfoData);
				});
			})
			.catch((err) => { });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const submit = (values) => {
		setLoading(true);
		investorRegService
			.submitBusinessInformation(values)
			.then((resp) => {
				let registrationModel = resp.data?.registrationModel;
				saveTokenFromRegistration(resp.data?.loginModel, serverNowFrom(resp));
				let regData = {
					...getRegistrationData(),
					step: registrationModel.step,
					requestedStep: 1,
				};
				setRegistrationData(regData);
				updateInvestorSteps(registrationModel.step);
				if (IS_SA && !isKsaInternationalInvestor) {
					setShowDeclarationPopup(true);
				} else {
					navigate(
						buildLocalizeRoute(
							`${AppRoutes.InvestorRegistration}/${AppRoutes.iRegDocumentVarification}`
						)
					);
				}
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const onBack = () => {
		let data = {
			...getRegistrationData(),
			requestedStep: 0,
		};
		setRegistrationData(data);
		onGoBack();
	};

	return (
		<Box>
			<SubStepper
				stepName="businessInformation"
				currentStep={2}
				totalSteps={2}
			/>
			<FormikProvider value={form}>
				<Box className="flex flex-col space-y-4">
					<Box className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<InputWrapper
							errorStateTestID="businessNameError"
							name={'businessName'}
							key={'businessName'}
							label={`${t('businessTradingName')} ${t('ofSignatory')}`}></InputWrapper>
						<InputWrapper
							errorStateTestID="tradeLicenseNumberError"
							name={'tradeLicenseNumber'}
							key={'tradeLicenseNumber'}
							label={`${t('tradeLicenseNumber')} ${t('ofSignatory')}`}
						></InputWrapper>
						<SelectWrapper
							hideRequiredIndicator={true}
							errorStateTestID='registrationCountryIdError'
							name={'registrationCountryId'}
							key={'registrationCountryId'}
							items={countries}
							label={`${t('countryOfRegistration')} ${t('ofSignatory')}`}

						/>
						<SelectWrapper
							items={countries}
							name={`taxResidencies.${0}.countryId`}
							key={`taxResidencies.${0}.countryId`}
							label={t('taxResidency')}
						/>
						<SelectWrapper
							items={[{ name: "Yes", displayName: "Yes", id: true },
							{ name: "No", displayName: "No", id: false }
							]}
							name={`taxResidencies.${0}.hasTaxId`}
							key={`taxResidencies.${0}.hasTaxId`}
							label={t('doYouHaveTaxIdInThisCountry')}
						/>
						<Box className='flex items-center'>
							{form.values.taxResidencies[0]?.hasTaxId === true ? (
								<InputWrapper
									name={`taxResidencies.${0}.taxId`}
									key={`taxResidencies.${0}.taxId`}
									label={t('taxId')}></InputWrapper>
							) : form.values.taxResidencies[0]?.hasTaxId === false ? (
								<SelectWrapper
									items={crsWhyNotQuestionsList}
									name={`taxResidencies.${0}.whyNotId`}
									key={`taxResidencies.${0}.whyNotId`}
									label={t('whyNot')}
								/>
							) : null}
						</Box>
					</Box>
					<Box className="flex md:justify-between justify-center">
						<ButtonWrapper
							dataTestID="backBtn"
							classList="!secondary-btn md:!mx-0 !mx-2"
							disabled={loading}
							largeBtn
							icon={<ArrowIcons isInvert></ArrowIcons>}
							label={t('back')}
							onClick={() => {
								onBack();
							}}
						/>
						<ButtonWrapper
							dataTestID="nextBtn"
							loading={loading}
							largeBtn
							endIcon={<ArrowIcons></ArrowIcons>}
							label={t('next')}
							onClick={() => {
								FormHelper.validateForm(form);
							}}
						/>
					</Box>
					{showDeclarationPopup ? (
						<InvestorDeclarationDialog
							state={showDeclarationPopup}
							onClose={(e) => {
								setShowDeclarationPopup(false);
							}}></InvestorDeclarationDialog>
					) : null}
				</Box>
			</FormikProvider>
		</Box>
	);
};
